import type { ReactNode } from 'react';
import { Card, Stack, Title, Text, Badge, Group } from '@repletoryreach/sdk-ui';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { TenantName } from '../../../../components/TenantName';
import { CopyableId } from '../../../../components/CopyableId';
import { RunStatusBadge } from '../../components/RunStatusBadge';
import { formatDuration } from '../../lib/duration';
import { useT } from '../../../../i18n/I18nProvider';

type FlowRun = NonNullable<Awaited<ReturnType<typeof admin.listFlowRuns>>['data']>[number];

export function OverviewTab({ run }: { run: FlowRun }) {
  const t = useT();
  return (
    // Two columns where the viewport allows it: the design keeps a detail
    // page's panels side by side so the whole record is one screen rather
    // than a column of cards a person has to scroll through.
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('flows.view.overview.runDetails')}</Title>
          <Row
            label={t('flows.columns.flow')}
            value={<Text role="bodySmall" fw={600}>{run.flow_name ?? t('flows.flowDeleted')}</Text>}
            hint={t('flows.view.overview.flowHint')}
          />
          <Row label={t('common.status')} value={<RunStatusBadge status={run.status} />} />
          <Row label={t('flows.columns.channel')} value={run.channel} />
          <Row label={t('common.tenant')} value={<TenantName id={run.organisation_id} withCopy />} />
          <Row
            label={t('flows.columns.contact')}
            value={run.contact_msisdn ?? <Muted>{t('common.notAvailable')}</Muted>}
          />
          <Row
            label={t('flows.view.overview.conversation')}
            value={
              run.conversation_id
                ? <CopyableId value={run.conversation_id} label={t('flows.copyConversationId')} chars={12} />
                : <Muted>{t('flows.view.overview.noConversation')}</Muted>
            }
          />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('flows.view.overview.references')}</Title>
          <Row
            label={t('flows.view.overview.runId')}
            value={<CopyableId value={run.id} label={t('flows.copyRunId')} chars={12} />}
            hint={t('flows.view.overview.runIdHint')}
          />
          <Row
            label={t('flows.view.overview.flowId')}
            value={<CopyableId value={run.flow_id} label={t('flows.copyFlowId')} chars={12} />}
          />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('flows.view.overview.timing')}</Title>
          <Row label={t('flows.columns.startedAt')} value={`${formatDateTime(run.started_at)} (${formatRelative(run.started_at)})`} />
          <Row
            label={t('flows.columns.finishedAt')}
            value={
              run.finished_at
                ? `${formatDateTime(run.finished_at)} (${formatRelative(run.finished_at)})`
                : <Badge size="xs" variant="dot" color="blue">{t('flows.view.overview.stillRunning')}</Badge>
            }
          />
          <Row
            label={t('flows.columns.duration')}
            value={formatDuration(run.started_at, run.finished_at)}
          />
        </Stack>
      </Card>
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <Text role="bodySmall" c="var(--rr-color-text-muted)">{children}</Text>;
}

function Row({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Stack gap="xs">
        <Group gap="xs" align="baseline">{value}</Group>
        {hint && <Text role="caption" c="var(--rr-color-text-muted)">{hint}</Text>}
      </Stack>
    </div>
  );
}
